// Files on Share as more than one page shows them (RFC-054, byj-1wv.51.20):
// how a size is said, the calls /files makes, and a file put as a new one of
// the person's — used by /files, a file's page, the class page and the admin's
// storage, so none of them imports another page.
import { api, bytesOf, type HandInReview } from "./api";
import { phrases, t, word } from "./i18n";

/** What puts a file or a folder from a terminal. */
export const putCommand = "byj file put PATH";

/** n bytes as a person reads them: a quota in GB. */
export const fileBytes = (n: number) => (n >= 1 << 30 ? `${Number((n / (1 << 30)).toFixed(1))} GB` : phrases.templateBytes(n));

/** The calls /files makes, so a test can supply its own. */
export type FilesApi = Pick<typeof api, "files" | "createFile" | "uploadFile" | "deleteFile" | "describeFile">;

/**
 * Puts a file the person chose as a new file of theirs, and opens it: the
 * file is made, then its bytes uploaded; when the upload is refused — the
 * quota, the cap — the file just made is deleted, so nothing empty is left.
 */
export async function putNewFile(client: Pick<FilesApi, "createFile" | "uploadFile" | "deleteFile">, f: File, assignment?: string): Promise<string> {
  const made = await client.createFile(f.name, "file", "", assignment);
  try {
    await client.uploadFile(made.id, await bytesOf(f));
  } catch (err) {
    await client.deleteFile(made.id).catch(() => undefined);
    throw err;
  }
  return made.id;
}

/** The calls a file's page makes, so a test can supply its own. */
export type FileApi = Pick<
  typeof api,
  | "file"
  | "describeFile"
  | "deleteFile"
  | "deleteFileVersion"
  | "uploadFile"
  | "folderEntries"
  | "createFileInvite"
  | "openInvites"
  | "revokeInvite"
  | "replaceInvite"
  | "addFilePeople"
  | "removeFileViewer"
  | "giveFile"
  | "takeFile"
  | "classes"
  | "fileComments"
  | "commentOnFile"
  | "resolveFileComment"
  | "reviewHandIn"
  | "clearHandInReview"
>;

/**
 * A hand-in's check in a few words (byj-1wv.136): checked or returned, its
 * mark, and — when a version came after the one read — that it did.
 */
export function reviewWords(r: HandInReview, latest: number): string {
  return [word("files:reviewState", r.state), r.mark && t("files:reviewMark", { mark: r.mark }), latest > r.version && t("files:reviewNewer", { n: latest })]
    .filter(Boolean)
    .join(" · ");
}
